import { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db, auth } from "../../firebase";
import {
  ensureChat,
  markRead,
  sendMessage,
  subscribeChatMeta,
  subscribeMessages,
} from "../../chat/chatService";
import s from "./BusinessChatsPage.module.css";

export default function BusinessChatsPage() {
  const [partners, setPartners] = useState([]);
  const [unreads, setUnreads] = useState({});
  const [selected, setSelected] = useState(null); // { uid, user }
  const [search, setSearch] = useState("");

  // завантаження списку бізнес-партнерів
  useEffect(() => {
    const qPartners = query(
      collection(db, "users"),
      where("category", "==", "Бізнес-партнер")
    );
    const unsub = onSnapshot(qPartners, (snap) => {
      const list = snap.docs.map((d) => {
        const data = d.data();
        // uid може бути у полі або дорівнювати id документа
        const uid = data.uid || d.id;
        return { id: d.id, uid, ...data };
      });
      setPartners(list);
    });
    return () => unsub();
  }, []);

  // підписки на мета-дані чатів (непрочитане)
  useEffect(() => {
    // скасовуємо попередні підписки
    const unsubs = partners.map((u) =>
      subscribeChatMeta(u.uid, (meta) => {
        setUnreads((prev) => ({ ...prev, [u.uid]: meta?.unreadForAdmin || 0 }));
      })
    );
    return () => unsubs.forEach((fn) => fn && fn());
  }, [partners]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = q
      ? partners.filter((u) => {
          const s1 = (u.displayName || "").toLowerCase();
          const s2 = (u.email || "").toLowerCase();
          return s1.includes(q) || s2.includes(q) || (u.uid || "").includes(q);
        })
      : partners.slice();
    // спершу з непрочитаними
    arr.sort((a, b) => (unreads[b.uid] || 0) - (unreads[a.uid] || 0));
    return arr;
  }, [partners, search, unreads]);

  const openChat = async (u) => {
    await ensureChat(u.uid);
    setUnreads((prev) => ({ ...prev, [u.uid]: 0 }));
    setSelected({ uid: u.uid, user: u });
    await markRead(u.uid, "admin");
  };

  return (
    <div className={s.wrap}>
      <aside className={s.sidebar}>
        <div className={s.sidebarHead}>
          <div className={s.title}>Бізнес-клієнти</div>
          <input
            className={s.search}
            placeholder="Пошук по імені/емейлу/uid…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className={s.list}>
          {filtered.map((u) => {
            const unread = unreads[u.uid] || 0;
            return (
              <div
                key={u.uid}
                className={`${s.item} ${
                  selected?.uid === u.uid ? s.active : ""
                }`}
              >
                <div className={s.meta} onClick={() => openChat(u)}>
                  <div className={s.name}>
                    {u.displayName || u.email || u.uid}
                  </div>
                  <div className={s.sub}>
                    {u.email && <span>{u.email}</span>}
                    {u.phone && <span> · {u.phone}</span>}
                  </div>
                </div>

                {unread > 0 ? (
                  <button
                    className={`${s.btn} ${s.btnWarn}`}
                    onClick={() => openChat(u)}
                  >
                    Відкрити <span className={s.badge}>{unread}</span>
                  </button>
                ) : (
                  <button className={s.btn} onClick={() => openChat(u)}>
                    Почати чат
                  </button>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className={s.empty}>Нічого не знайдено</div>
          )}
        </div>
      </aside>

      <main className={s.main}>
        {selected ? (
          <AdminChatWindow
            uidPartner={selected.uid}
            user={selected.user}
            key={selected.uid}
          />
        ) : (
          <div className={s.placeholder}>
            Оберіть клієнта зліва й натисніть «Почати чат»
          </div>
        )}
      </main>
    </div>
  );
}

/* ===========================
   Вікно чату для адміна
   =========================== */
function AdminChatWindow({ uidPartner, user }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    const unsub = subscribeMessages(uidPartner, (list) => {
      setMsgs(list);
      // автоскрол
      setTimeout(() => {
        listRef.current?.scrollTo({
          top: listRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 0);
      const last = list[list.length - 1];
      if (last?.from === "user") {
        markRead(uidPartner, "admin");
      }
    });
    markRead(uidPartner, "admin");
    return () => unsub && unsub();
  }, [uidPartner]);

  const onSend = async () => {
    await sendMessage(uidPartner, {
      text,
      from: "admin",
      senderId: auth?.currentUser?.uid || "admin",
    });
    setText("");
  };

  return (
    <div className={s.chat}>
      <div className={s.chatHead}>
        <div className={s.chatTitle}>
          {user?.displayName || user?.email || uidPartner}
        </div>
        <div className={s.chatSub}>Чат із бізнес-партнером</div>
      </div>

      <div className={s.chatList} ref={listRef}>
        {msgs.map((m) => (
          <div
            key={m.id}
            className={`${s.msg} ${m.from === "admin" ? s.me : s.them}`}
          >
            <div className={s.bubble}>{m.text}</div>
          </div>
        ))}
      </div>

      <div className={s.inputRow}>
        <input
          className={s.input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Напишіть повідомлення…"
          onKeyDown={(e) => e.key === "Enter" && text.trim() && onSend()}
        />
        <button className={s.sendBtn} onClick={onSend} disabled={!text.trim()}>
          Надіслати
        </button>
      </div>
    </div>
  );
}
